import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { analyticsEvents, track } from '@/lib/analytics/posthog';
import { INTENT_OPTIONS, isNewAccount, type IntentAnswer, type IntentArm } from '@/lib/personas/intent';
import { cn } from '@/lib/utils';
import { isStudioHost } from '@/routing/studioHost';

interface IntentState {
  arm: IntentArm;
  declaredAt: string | null;
  skippedAt: string | null;
  accountCreatedAt: string | null;
}

const HIDDEN_PATHS = /^\/(auth|onboarding|admin|signup|verify-email|forgot-password|reset-password|embed)(\/|$)/;

function shouldAsk(state: IntentState | null): state is IntentState {
  if (!state || state.arm !== 'asked' || state.declaredAt || state.skippedAt) return false;
  return isNewAccount(state.accountCreatedAt);
}

/**
 * Asks a new account what brought them here, once. Mounted app-wide (not in
 * the signup form) so Google and Facebook signups see it on their first
 * signed-in page. Half of accounts (the `control` arm) are never asked.
 */
/** `suppressed` holds the prompt back while another dialog (the post-confirmation
 *  welcome modal) is open; it shows once that closes. */
export default function IntentPrompt({ suppressed = false }: { suppressed?: boolean }) {
  const { user, getAccessToken } = useAuth();
  const { pathname } = useLocation();
  // Tagged with the user id so a different sign-in never reuses old answers.
  const [fetched, setFetched] = useState<(IntentState & { userId: string }) | null>(null);
  const [doneFor, setDoneFor] = useState<string | null>(null);
  const [answers, setAnswers] = useState<IntentAnswer[]>([]);
  const [city, setCity] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shownFor = useRef<string | null>(null);

  const userId = user?.id;
  const state = fetched && fetched.userId === userId ? fetched : null;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        // Accounts 7+ days old are never asked: skip the request entirely.
        const { data } = await supabase.auth.getSession();
        const session = data.session;
        if (!session?.access_token || !isNewAccount(session.user?.created_at)) return;
        const response = await fetch('/api/me/intent', { headers: { Authorization: `Bearer ${session.access_token}` } });
        if (!response.ok || cancelled) return;
        setFetched({ ...((await response.json()) as IntentState), userId });
      } catch {
        // No prompt this visit; it can show on a later one.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const done = Boolean(userId) && doneFor === userId;
  const open = !suppressed && !done && shouldAsk(state) && !HIDDEN_PATHS.test(pathname) && !isStudioHost();
  const arm = state?.arm;

  useEffect(() => {
    if (open && userId && shownFor.current !== userId) {
      shownFor.current = userId;
      track(analyticsEvents.INTENT_PROMPT_SHOWN, { arm });
    }
  }, [open, arm, userId]);

  const post = async (payload: Record<string, unknown>) => {
    const token = await getAccessToken();
    const response = await fetch('/api/me/intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`intent save failed: ${response.status}`);
  };

  const wantsCity = answers.includes('visit');
  const cityValue = wantsCity ? city.trim() : '';

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await post(cityValue ? { answers, city: cityValue } : { answers });
      track(analyticsEvents.INTENT_PROMPT_ANSWERED, { answers, has_city: Boolean(cityValue), arm });
      setDoneFor(userId ?? null);
    } catch {
      setError("Couldn't save that. Try again, or skip for now.");
    } finally {
      setSaving(false);
    }
  };

  // Skip for now, X and Escape count as a skip, so it does not come back on
  // every page; a click outside does not. A failed save is not retried.
  const skip = () => {
    if (done || saving) return;
    setDoneFor(userId ?? null);
    track(analyticsEvents.INTENT_PROMPT_SKIPPED, { arm });
    post({ answers: [], skipped: true }).catch(() => {});
  };

  const toggle = (value: IntentAnswer) =>
    setAnswers((current) => (current.includes(value) ? current.filter((a) => a !== value) : [...current, value]));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && skip()}>
      <DialogContent
        className="max-h-[90dvh] max-w-md gap-5 overflow-y-auto bg-card"
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">What brings you to Light Hunt?</DialogTitle>
          <DialogDescription>So we only send you what's relevant. You can skip this.</DialogDescription>
        </DialogHeader>

        <div role="group" className="grid gap-2" aria-label="Pick any that fit">
          {INTENT_OPTIONS.map((option) => {
            const selected = answers.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={selected}
                onClick={() => toggle(option.value)}
                className={cn(
                  'flex w-full items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors motion-reduce:transition-none',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                  selected ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/60',
                )}
              >
                {/* A bulb that lights when picked. */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-1 h-3 w-3 shrink-0 rounded-full border transition-[background-color,box-shadow] motion-reduce:transition-none',
                    selected
                      ? 'border-primary bg-primary shadow-[0_0_10px_2px_hsl(var(--primary)/0.55)]'
                      : 'border-muted-foreground/50 bg-transparent',
                  )}
                />
                <span className="grid gap-0.5">
                  <span className="text-sm font-semibold text-foreground">{option.label}</span>
                  <span className="text-sm text-muted-foreground">{option.hint}</span>
                </span>
              </button>
            );
          })}
        </div>

        {wantsCity && (
          <div className="grid gap-2">
            <Label htmlFor="intent-city">Your city (optional)</Label>
            <Input
              id="intent-city"
              value={city}
              maxLength={80}
              autoComplete="address-level2"
              placeholder="Pasadena, CA"
              onChange={(event) => setCity(event.target.value)}
            />
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="ghost" onClick={skip} disabled={saving}>
            Skip for now
          </Button>
          <Button type="button" onClick={submit} disabled={answers.length === 0 || saving}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
